import { TFilterField, TFilterValue } from "@irohana-dev/components";
import { TOption } from "@/types/common";
import { applyColumnSettings as applyKitColumnSettings, asStringArray, buildColumnOptions, fromDateRangeValue } from "@/components/commons/filter-condition/filter-condition-adapter-kit";
import {
  PAST_JOBS_MULTI_SELECT_FILTER_KEYS,
  TJobDisplayColumnSetting,
  TPastJobsFilterForm,
  defaultPastJobsDisplayColumns,
  defaultPastJobsFilterValues,
} from "@/features/company/components/company-detail/past-jobs/past-jobs-shared";

const DATE_RANGE_KEY = "endDate";

type TPastJobsFilterOptions = {
  status: TOption[];
  jobName: TOption[];
  workplace: TOption[];
};

export const buildPastJobsFilterFields = (options: TPastJobsFilterOptions): TFilterField[] => [
  { type: "freeText", key: "jobName", label: "求人名", options: options.jobName, placeholder: "入力してください" },
  { type: "multiSelect", key: "status", label: "ステータス", options: options.status, placeholder: "選択してください" },
  { type: "text", key: "totalAmount", label: "総支給額", placeholder: "入力してください", suffix: "円", inputMode: "numeric" },
  { type: "freeText", key: "workplace", label: "勤務場所", options: options.workplace, placeholder: "入力してください" },
  { type: "text", key: "caseCount", label: "応募数", placeholder: "入力してください", suffix: "人", inputMode: "numeric" },
  { type: "text", key: "recruitmentNumber", label: "募集人数", placeholder: "入力してください", suffix: "人", inputMode: "numeric" },
  { type: "dateRange", key: DATE_RANGE_KEY, label: "募集期限" },
];

export const buildPastJobsColumnOptions = buildColumnOptions;

/** The panel keeps a single `endDate` range; the form keeps two separate keys. */
export const toFilterValue = (filters: TPastJobsFilterForm): TFilterValue => {
  const value: TFilterValue = {};
  for (const [key, raw] of Object.entries(filters)) {
    if (key === "endDateFrom" || key === "endDateTo") continue;
    if (Array.isArray(raw)) {
      if (raw.length > 0) value[key] = raw.map(String);
      continue;
    }
    if (raw === "" || raw === null || raw === undefined) continue;
    // Conditions saved before these fields became multi-select hold a scalar. The grid reads
    // a non-array as empty, so leaving it through would show a blank control while the stale
    // value stayed in the filter and kept reaching the query.
    if (PAST_JOBS_MULTI_SELECT_FILTER_KEYS.includes(key as keyof TPastJobsFilterForm)) {
      value[key] = [String(raw)];
      continue;
    }
    value[key] = String(raw);
  }
  const { endDateFrom, endDateTo } = filters;
  if (endDateFrom || endDateTo) {
    value[DATE_RANGE_KEY] = { from: endDateFrom, to: endDateTo };
  }
  return value;
};

export const fromFilterValue = (value: TFilterValue): TPastJobsFilterForm => {
  const range = fromDateRangeValue(value, DATE_RANGE_KEY);
  return {
    ...defaultPastJobsFilterValues,
    jobName: (value.jobName as string) ?? "",
    status: asStringArray(value.status),
    totalAmount: (value.totalAmount as string) ?? "",
    workplace: (value.workplace as string) ?? "",
    caseCount: (value.caseCount as string) ?? "",
    recruitmentNumber: (value.recruitmentNumber as string) ?? "",
    endDateFrom: range.from,
    endDateTo: range.to,
  };
};

export const applyColumnSettings = (columns: TJobDisplayColumnSetting[], order: string[], hidden: string[]): TJobDisplayColumnSetting[] =>
  applyKitColumnSettings(columns, order, hidden, defaultPastJobsDisplayColumns);
